// V12.4: motor de descontos no cliente — espelha core.compute_listing_discount do backend.
// Aplica o MELHOR desconto p/ o cliente (sem somar) sobre o subtotal das diárias.
// JS getDay(): 0=Dom ... 5=Sexta, 1=Segunda.
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const hhmmToMin = (s) => {
  if (!s) return null;
  const [h, m] = String(s).split(":");
  const v = parseInt(h, 10) * 60 + parseInt(m || "0", 10);
  return Number.isFinite(v) ? v : null;
};

// Índices das diárias de fim de semana + nº de fins de semana distintos (runs).
// getDay(): 0=Dom, 5=Sexta, 6=Sábado. Sexta só conta se retirada >= pickupFrom.
function weekendIndices(startDate, units, pickupTime, pickupFrom) {
  const sd = new Date(`${String(startDate).slice(0, 10)}T12:00:00`);
  if (isNaN(sd)) return { idx: [], runs: 0 };
  const pt = hhmmToMin(pickupTime);
  const pf = hhmmToMin(pickupFrom);
  const idx = [];
  for (let i = 0; i < units; i++) {
    const d = new Date(sd); d.setDate(sd.getDate() + i);
    const wd = d.getDay();
    if (wd === 6 || wd === 0) idx.push(i);           // sábado / domingo
    else if (wd === 5 && pt != null && pf != null && pt >= pf) idx.push(i); // sexta a partir do horário
  }
  let runs = 0, prev = null;
  idx.forEach((i) => { if (prev === null || i !== prev + 1) runs += 1; prev = i; });
  return { idx, runs };
}

export function computeListingDiscount({
  pricePerDay, timeUnits, quantity = 1, startDate, endDate,
  tipoCobranca = "DIARIO", discountRules = [], weekendPromo = null, pickupTime = null,
  quantityDiscountRules = [],
}) {
  const ppd = round2(pricePerDay || 0);
  const units = Math.max(1, parseInt(timeUnits || 1, 10));
  const qty = Math.max(1, parseInt(quantity || 1, 10));
  const gross = round2(ppd * units * qty);
  const out = {
    gross, valor_diarias: gross, discount_amount: 0,
    discount_pct: null, discount_type: null, discount_label: null, nudge: null, quantity_nudge: null,
  };
  const isDaily = (tipoCobranca || "").toUpperCase() !== "MENSAL";
  const rules = (discountRules || []).filter((r) => r && r.min_duration != null && r.discount_pct != null);
  const qrules = (quantityDiscountRules || []).filter((r) => r && r.min_quantity != null && r.discount_pct != null);
  const candidates = [];

  // 1) Desconto progressivo — maior pct alcançado
  let bestPct = 0;
  rules.forEach((r) => {
    const md = parseInt(r.min_duration, 10);
    const pct = parseFloat(r.discount_pct);
    if (md >= 1 && pct > 0 && pct <= 90 && units >= md && pct > bestPct) bestPct = pct;
  });
  if (bestPct > 0) {
    candidates.push({ val: round2(gross * (1 - bestPct / 100)), type: "progressive", label: `Desconto progressivo (-${bestPct}%)`, pct: bestPct });
  }

  // V18.9) Desconto por QUANTIDADE de itens — maior pct alcançado
  let bestQtyPct = 0;
  qrules.forEach((r) => {
    const mq = parseInt(r.min_quantity, 10);
    const pct = parseFloat(r.discount_pct);
    if (mq >= 2 && pct > 0 && pct <= 90 && qty >= mq && pct > bestQtyPct) bestQtyPct = pct;
  });
  if (bestQtyPct > 0) {
    candidates.push({ val: round2(gross * (1 - bestQtyPct / 100)), type: "quantity", label: `Desconto por quantidade (-${bestQtyPct}%)`, pct: bestQtyPct });
  }

  // 2) Promoção de fim de semana (parte de FDS de qualquer aluguel)
  const wp = weekendPromo || {};
  if (isDaily && wp.active && startDate) {
    const { idx, runs } = weekendIndices(startDate, units, pickupTime, wp.pickup_from);
    const W = idx.length;
    if (W > 0) {
      const nonWeekend = units - W;
      const wtype = (wp.type || "").toUpperCase();
      if (wtype === "FLAT_ONE_DAY") {
        candidates.push({ val: round2(ppd * qty * (nonWeekend + runs)), type: "weekend_flat", label: "Promoção de Fim de Semana (fim de semana pelo preço de 1 diária)", pct: null });
      } else if (wtype === "DISCOUNT_PERCENT") {
        const pct = parseFloat(wp.discount_pct || 0);
        if (pct > 0 && pct <= 90) {
          candidates.push({ val: round2(ppd * qty * (nonWeekend + W * (1 - pct / 100))), type: "weekend_percent", label: `Promoção de Fim de Semana (-${pct}% no fim de semana)`, pct });
        }
      }
    }
  }

  if (candidates.length) {
    const best = candidates.reduce((a, b) => (b.val < a.val ? b : a));
    if (best.val < gross) {
      out.valor_diarias = best.val;
      out.discount_amount = round2(gross - best.val);
      out.discount_pct = best.pct;
      out.discount_type = best.type;
      out.discount_label = best.label;
    }
  }

  // 3) Empurrãozinho (nudge): próxima faixa progressiva ainda não atingida
  let nxt = null;
  rules.forEach((r) => {
    const md = parseInt(r.min_duration, 10);
    const pct = parseFloat(r.discount_pct);
    if (md > units && pct > 0 && (bestPct === 0 || pct > bestPct)) {
      if (!nxt || md < nxt.min_duration) nxt = { min_duration: md, discount_pct: pct };
    }
  });
  if (nxt) out.nudge = { units_needed: nxt.min_duration - units, min_duration: nxt.min_duration, discount_pct: nxt.discount_pct };

  // V18.9 — Nudge do próximo tier de QUANTIDADE (independente do de duração)
  let qnxt = null;
  qrules.forEach((r) => {
    const mq = parseInt(r.min_quantity, 10);
    const pct = parseFloat(r.discount_pct);
    if (mq > qty && pct > 0 && (bestQtyPct === 0 || pct > bestQtyPct)) {
      if (!qnxt || mq < qnxt.min_quantity) qnxt = { min_quantity: mq, discount_pct: pct };
    }
  });
  if (qnxt) out.quantity_nudge = { qty_needed: qnxt.min_quantity - qty, min_quantity: qnxt.min_quantity, discount_pct: qnxt.discount_pct };

  return out;
}

// V12.4: descreve as promoções de um anúncio para etiquetas (cards) e seção de detalhes.
// Retorna { hasPromo, cardBadge, weekendBadge, lines:[{icon,text}] }.
const CYCLE_UNIT_LABEL = {
  day: ["dia", "dias"], week: ["semana", "semanas"],
  fortnight: ["quinzena", "quinzenas"], month: ["mês", "meses"],
};

// Rótulo (singular/plural) da unidade de cobrança do anúncio.
export function unitLabelForItem(item) {
  const cu = (item?.cycle_unit || "").toLowerCase();
  if (cu && CYCLE_UNIT_LABEL[cu]) return CYCLE_UNIT_LABEL[cu];
  const isMonthly = (item?.tipo_cobranca || "").toUpperCase() === "MENSAL";
  return isMonthly ? ["mês", "meses"] : ["dia", "dias"];
}

// Epic 1: motor de preço dos modos flexible/closed (espelha core._price_cycle).
// diária = preço_do_ciclo ÷ dias_do_ciclo; valor = diária × dias_faturáveis × qty.
// Desconto progressivo aplicado por Nº DE CICLOS (não por dias).
export function computeCyclePricing({ pricePerCycle, cycleDays, billableDays, nCycles, quantity = 1, discountRules = [], quantityDiscountRules = [] }) {
  const ppc = round2(pricePerCycle || 0);
  const cd = Math.max(1, parseInt(cycleDays || 1, 10));
  const bdays = Math.max(1, parseInt(billableDays || 1, 10));
  const qty = Math.max(1, parseInt(quantity || 1, 10));
  const dailyRate = ppc / cd;
  const gross = round2(dailyRate * bdays * qty);
  const out = { gross, valor_diarias: gross, discount_amount: 0, discount_pct: null,
    discount_type: null, discount_label: null, nudge: null, quantity_nudge: null, daily_rate: round2(dailyRate) };
  const rules = (discountRules || []).filter((r) => r && r.min_duration != null && r.discount_pct != null);
  const qrules = (quantityDiscountRules || []).filter((r) => r && r.min_quantity != null && r.discount_pct != null);
  const candidates = [];
  let bestPct = 0;
  rules.forEach((r) => {
    const md = parseInt(r.min_duration, 10); const pct = parseFloat(r.discount_pct);
    if (md >= 1 && pct > 0 && pct <= 90 && (nCycles || 0) >= md && pct > bestPct) bestPct = pct;
  });
  if (bestPct > 0) {
    candidates.push({ val: round2(gross * (1 - bestPct / 100)), type: "progressive", label: `Desconto progressivo (-${bestPct}%)`, pct: bestPct });
  }
  let bestQtyPct = 0;
  qrules.forEach((r) => {
    const mq = parseInt(r.min_quantity, 10); const pct = parseFloat(r.discount_pct);
    if (mq >= 2 && pct > 0 && pct <= 90 && qty >= mq && pct > bestQtyPct) bestQtyPct = pct;
  });
  if (bestQtyPct > 0) {
    candidates.push({ val: round2(gross * (1 - bestQtyPct / 100)), type: "quantity", label: `Desconto por quantidade (-${bestQtyPct}%)`, pct: bestQtyPct });
  }
  if (candidates.length) {
    const best = candidates.reduce((a, b) => (b.val < a.val ? b : a));
    if (best.val < gross) {
      out.valor_diarias = best.val;
      out.discount_amount = round2(gross - best.val);
      out.discount_pct = best.pct;
      out.discount_type = best.type;
      out.discount_label = best.label;
    }
  }
  let nxt = null;
  rules.forEach((r) => {
    const md = parseInt(r.min_duration, 10); const pct = parseFloat(r.discount_pct);
    if (md > (nCycles || 0) && pct > 0 && (bestPct === 0 || pct > bestPct)) {
      if (!nxt || md < nxt.min_duration) nxt = { min_duration: md, discount_pct: pct };
    }
  });
  if (nxt) out.nudge = { cycles_needed: Math.ceil(nxt.min_duration - (nCycles || 0)), min_duration: nxt.min_duration, discount_pct: nxt.discount_pct };
  let qnxt = null;
  qrules.forEach((r) => {
    const mq = parseInt(r.min_quantity, 10); const pct = parseFloat(r.discount_pct);
    if (mq > qty && pct > 0 && (bestQtyPct === 0 || pct > bestQtyPct)) {
      if (!qnxt || mq < qnxt.min_quantity) qnxt = { min_quantity: mq, discount_pct: pct };
    }
  });
  if (qnxt) out.quantity_nudge = { qty_needed: qnxt.min_quantity - qty, min_quantity: qnxt.min_quantity, discount_pct: qnxt.discount_pct };
  return out;
}

// "1 mês e 10 dias" — quebra os dias faturáveis em ciclos completos + dias restantes.
export function cycleBreakdownLabel(billableDays, cycleDays, cycleUnit) {
  const cd = Math.max(1, cycleDays || 1);
  const [sing, plur] = CYCLE_UNIT_LABEL[cycleUnit] || (cd === 30 ? ["mês", "meses"] : ["ciclo", "ciclos"]);
  const full = Math.floor(billableDays / cd);
  const rem = billableDays % cd;
  const parts = [];
  if (full > 0) parts.push(`${full} ${full === 1 ? sing : plur}`);
  if (rem > 0) parts.push(`${rem} ${rem === 1 ? "dia" : "dias"}`);
  return parts.join(" e ") || `${billableDays} dias`;
}

// Epic 2: descreve a política de FRETE do anúncio p/ exibir no detalhe (não só a cobrança).
// Retorna { badge, lines:[str] } — badge curto p/ a linha de infos; lines p/ um card.
export function describeFrete(item) {
  const out = { badge: null, lines: [] };
  const fc = item?.frete_config;
  const brl = (n) => `R$ ${Number(n || 0).toFixed(2).replace(".", ",")}`;
  if (!fc) {
    const v = parseFloat(item?.valor_frete || 0);
    if (v > 0) { out.badge = `Frete: ${brl(v)}`; out.lines.push(`Frete fixo de ${brl(v)}.`); }
    return out;
  }
  const tipo = (fc.tipo || "fixo").toLowerCase();
  const raio = parseFloat(fc.raio_gratis_km || 0);
  if (tipo === "gratis") { out.badge = "Frete grátis"; out.lines.push("Entrega com frete grátis."); return out; }
  if (tipo === "combinar") { out.badge = "Frete a combinar"; out.lines.push("Frete a combinar diretamente com o proprietário."); return out; }
  if (raio > 0) out.lines.push(`Frete grátis dentro de ${raio} km de distância.`);
  if (tipo === "fixo") {
    const v = parseFloat(fc.valor_fixo || 0);
    out.badge = raio > 0 ? `Grátis até ${raio}km` : `Frete: ${brl(v)}`;
    out.lines.push(raio > 0 ? `Acima de ${raio} km: ${brl(v)} (valor fixo).` : `Frete fixo de ${brl(v)}.`);
  } else if (tipo === "por_km") {
    const v = parseFloat(fc.valor_por_km || 0);
    const iv = fc.ida_volta ? " (ida e volta)" : "";
    out.badge = raio > 0 ? `Grátis até ${raio}km · ${brl(v)}/km` : `${brl(v)}/km${iv}`;
    out.lines.push(`Acima de ${raio > 0 ? raio + " km" : "0 km"}: ${brl(v)} por km${iv}.`);
  }
  return out;
}

export function describeListingPromos(item) {
  const out = { hasPromo: false, cardBadge: null, weekendBadge: null, lines: [] };
  if (!item) return out;
  const isDaily = (item.tipo_cobranca || "DIARIO").toUpperCase() !== "MENSAL";
  const [uSing, uPlur] = unitLabelForItem(item);
  const rules = [...(item.discount_rules || [])]
    .filter((r) => r && r.min_duration != null && r.discount_pct != null)
    .sort((a, b) => parseInt(a.min_duration, 10) - parseInt(b.min_duration, 10));
  if (rules.length > 0) {
    out.hasPromo = true;
    const minDur = parseInt(rules[0].min_duration, 10);
    out.cardBadge = `Descontos a partir de ${minDur} ${minDur === 1 ? uSing : uPlur}`;
    rules.forEach((r) => {
      const md = parseInt(r.min_duration, 10);
      out.lines.push({ type: "progressive", text: `A partir de ${md} ${md === 1 ? uSing : uPlur} → ${parseFloat(r.discount_pct)}% de desconto` });
    });
  }
  // V18.9 — Descontos por QUANTIDADE de itens
  const qrules = [...(item.quantity_discount_rules || [])]
    .filter((r) => r && r.min_quantity != null && r.discount_pct != null)
    .sort((a, b) => parseInt(a.min_quantity, 10) - parseInt(b.min_quantity, 10));
  if (qrules.length > 0) {
    out.hasPromo = true;
    const minQ = parseInt(qrules[0].min_quantity, 10);
    if (!out.cardBadge) out.cardBadge = `Descontos a partir de ${minQ} itens`;
    qrules.forEach((r) => {
      const mq = parseInt(r.min_quantity, 10);
      out.lines.push({ type: "quantity", text: `A partir de ${mq} itens → ${parseFloat(r.discount_pct)}% de desconto` });
    });
  }
  const wp = item.weekend_promo || {};
  if (isDaily && wp.active) {
    out.hasPromo = true;
    const wtype = (wp.type || "").toUpperCase();
    const win = wp.pickup_from ? ` (sexta a partir de ${wp.pickup_from}${wp.return_until ? `, devolução até segunda ${wp.return_until}` : ""})` : "";
    if (wtype === "FLAT_ONE_DAY") {
      out.weekendBadge = "Fim de semana: preço de 1 diária";
      out.lines.push({ type: "weekend", text: `Fim de semana: as diárias de fim de semana saem pelo preço de 1 diária${win}` });
    } else if (wtype === "DISCOUNT_PERCENT") {
      const pct = parseFloat(wp.discount_pct || 0);
      out.weekendBadge = `Fim de semana -${pct}%`;
      out.lines.push({ type: "weekend", text: `Fim de semana: ${pct}% de desconto nas diárias de fim de semana${win}` });
    }
  }
  return out;
}

/**
 * V17: intervalo de preço efetivo por ciclo considerando variantes.
 * - Variante obrigatória (required): força o menor/maior price_delta no cálculo do mín/máx.
 * - Variante opcional: mín não muda (usuário pode não escolher a opção mais cara);
 *   o máx sobe pelo maior price_delta positivo (usuário pode escolher a mais cara).
 * Retorna { min, max, hasRange } — hasRange=true se min !== max e há variantes.
 */
export function getListingPriceRange(item) {
  const base = parseFloat(item?.price_per_day || 0) || 0;
  const comps = (item?.complements || []).filter((c) => (c?.kind || "").toLowerCase() === "variant");
  let min = base;
  let max = base;
  for (const c of comps) {
    const deltas = (c.options || []).map((o) => parseFloat(o?.price_delta || 0) || 0);
    if (!deltas.length) continue;
    const dMin = Math.min(...deltas);
    const dMax = Math.max(...deltas);
    if (c.required) {
      min += dMin;
      max += dMax;
    } else {
      // Opcional: usuário pode não escolher → mantém min; máx sobe se houver delta positivo
      if (dMax > 0) max += dMax;
      if (dMin < 0) min += dMin; // opção que reduz também empurra o mín
    }
  }
  min = Math.max(0, Math.round(min * 100) / 100);
  max = Math.max(min, Math.round(max * 100) / 100);
  return { min, max, hasRange: min !== max && comps.length > 0, base };
}

